<script setup lang="ts">
import { eventBus } from "#bb/lib/eventBus";
import { buildYouTubeEmbedUrl } from "#bb/lib/youtube";
import { cn } from "@/lib/utils";

const props = defineProps<{ id: string }>();

const { getContentById } = useContentManager();
const definitionData = computed(() => getContentById(props.id) || {});

const commands = [
  {
    id: "editors-group",
    type: "group",
    name: "Editors",
    description: "YouTube video editor",
    commands: [
      {
        id: "edit",
        name: "Edit",
        description: "Edit the YouTube video settings",
        action: async () => {
          eventBus.emit(`component:onEdit:${props.id}`, {
            data: definitionData.value,
          });
        },
        icon: "lucide:edit",
      },
    ],
  },
];

defineExpose({
  commands,
  properties: {
    id: props.id,
    name: "YouTube",
    icon: "lucide:youtube",
  },
});

const embedUrl = computed(() =>
  buildYouTubeEmbedUrl(definitionData.value.source, {
    privacyEnhanced: definitionData.value.privacyEnhanced !== false,
    autoplay: definitionData.value.autoplay === true,
    controls: definitionData.value.controls !== false,
    loop: definitionData.value.loop === true,
    muted: definitionData.value.muted === true,
    startSeconds: definitionData.value.startSeconds,
  }),
);

const iframeTitle = computed(() => {
  const title = definitionData.value.title;
  return typeof title === "string" && title.trim()
    ? title.trim()
    : "YouTube video";
});

const configuredWidth = computed(() => {
  const width = Number(definitionData.value.width);
  return Number.isFinite(width) && width > 0 ? Math.round(width) : null;
});
const configuredHeight = computed(() => {
  const height = Number(definitionData.value.height);
  return Number.isFinite(height) && height > 0 ? Math.round(height) : null;
});
const containerStyle = computed(() => ({
  width: configuredWidth.value ? `${configuredWidth.value}px` : undefined,
  maxWidth: "100%",
}));
const mediaStyle = computed(() => ({
  height: configuredHeight.value ? `${configuredHeight.value}px` : undefined,
}));
</script>

<template>
  <div
    :class="cn(configuredWidth ? '' : 'w-full', $attrs.class ?? '')"
    :style="containerStyle"
  >
    <div
      v-if="embedUrl"
      :class="[
        'w-full overflow-hidden rounded-lg bg-muted',
        configuredHeight ? '' : 'aspect-video',
      ]"
      :style="mediaStyle"
    >
      <iframe
        class="size-full border-0"
        :src="embedUrl"
        :title="iframeTitle"
        loading="lazy"
        referrerpolicy="strict-origin-when-cross-origin"
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
        allowfullscreen
      />
    </div>

    <div
      v-else
      :class="[
        'flex w-full items-center justify-center rounded-lg border border-dashed bg-muted/40 px-6 text-center',
        configuredHeight ? '' : 'aspect-video',
      ]"
      :style="mediaStyle"
      role="status"
    >
      <div class="space-y-2">
        <Icon name="lucide:youtube" class="mx-auto size-8 text-muted-foreground" />
        <p class="text-sm font-medium text-foreground">YouTube video unavailable</p>
        <p class="text-xs text-muted-foreground">
          Add a valid YouTube URL or video ID in the block editor.
        </p>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { ComponentDefinitionIds } from "#bb/lib/componentDefinitionIds";

export default {
  componentId: ComponentDefinitionIds.YOUTUBE,
  editor: () => import("#bb/components/editor/media/youtube.vue"),
  icon: "lucide:youtube",
  type: "YouTube",
  description: "Embed a responsive YouTube video from an approved URL or video ID.",
  default: {
    source: "",
    title: "YouTube video",
    privacyEnhanced: true,
    autoplay: false,
    controls: true,
    loop: false,
    muted: false,
    startSeconds: 0,
    width: 0,
    height: 0,
  },
  categories: ["Media"],
  order: 13054,
};
</script>
